"use client";

import { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { toggleVariants } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
export type SplitChipSegment = {
  value: string;
  label: ReactNode;
  icon?: LucideIcon;
};
type Props = {
  /** Two or more mutually-exclusive segments rendered as one divided pill. */
  segments: SplitChipSegment[];
  /** Selected segment value, or null when nothing is picked. */
  value: string | null;
  /** Selecting the active segment again clears it (emits null). */
  onValueChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "default" | "md" | "xs";
};

/**
 * A single pill split into mutually-exclusive segments (e.g. Wi-Fi | Fibra).
 * Each segment is a plain button styled with the shared `toggleVariants`
 * (`outlined`), so selection reads exactly like the sibling chips — border-only
 * active state — without nesting a ToggleGroup. Single-select with toggle-off.
 */
export function SplitChip({
  segments,
  value,
  onValueChange,
  disabled,
  className,
  size
}: Props) {
  return <div role="group" className={cn("inline-flex", className)} data-sentry-component="SplitChip" data-sentry-source-file="SplitChip.tsx">
      {segments.map(({
      value: v,
      label,
      icon: Icon
    }, i) => {
      const active = value === v;
      return <button key={v} type="button" disabled={disabled} data-state={active ? "on" : "off"} aria-pressed={active} onClick={() => onValueChange(active ? null : v)} className={cn(toggleVariants({
        variant: "outlined",
        size
      }), "relative gap-1.5 whitespace-nowrap rounded-none data-[state=on]:z-10", i === 0 && "rounded-l-full", i === segments.length - 1 && "rounded-r-full", i > 0 && "-ml-0.5", disabled && "pointer-events-none opacity-50")}>
            {Icon && <Icon className="size-4" />}
            {label}
          </button>;
    })}
    </div>;
}